import React, {useEffect, useState} from 'react';
import axios from "axios";
import config from '../../config';
import './Registration.css';
import logo2 from '../../images/logo2.png';
import RegistrationMobile from "./RegistrationMobile/RegistrationMobile";
import {useTranslation} from "react-i18next";
import scrollToTop from "../../scripts/scroll-to-top";

interface RegistrationProps {
    setNewCurrentStep(step: number): void
    openModal(text: string): void
    setRegistrationFormFilled(state: boolean): void
    registrationFormFilled: boolean
    registrationData: any
    setRegistrationData(data: any): void
    isMobileScreen: boolean
}

function Registration(props: RegistrationProps) {
    const { t, i18n } = useTranslation();

    const [fields, setFields] = useState<any>()
    const [name, setName] = useState('');
    const [yearOfBirth, setYearOfBirth] = useState<number>();
    const [selectedGender, setSelectedGender] = useState('');
    const [formError, setFormError] = useState<string | null>(null);
    const [loaderIsActive, setLoaderIsActive] = useState(false);

    const handleGenderChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSelectedGender(event.target.value);
    };
    const getPageContent = async () => {
        try {
            const response = await axios.post(
                config.API_URL_FOR_LOCAL_DEVELOPMENT + `/api/v1/landing/user/fields`,
                {
                    language: config.LANGUAGE || 'en'
                }
            );

            setFields(response.data.fields);
        } catch (error) {
            console.error('Error while retrieving data:', error);
        }
    }

    const submitForm = async () => {
        setFormError(null);

        if (!name || !yearOfBirth || !selectedGender) {
            setFormError(t('Complete the required field'))
            return;
        }

        const age = new Date().getFullYear() - yearOfBirth;

        if (!conditionsArePassed(age, selectedGender)) return;

        try {
            setLoaderIsActive(true);
            scrollToTop();

            const response = await axios.post(
                config.API_URL_FOR_LOCAL_DEVELOPMENT + '/api/v1/landing/user/store',
                {
                    language: config.LANGUAGE || 'en',
                    session: config.SESSION,
                    name,
                    yearOfBirth,
                    genderAtBirth: selectedGender,
                    source: 'Web'
                }
            );

            window.localStorage.setItem('landingUserId', response.data.id);
            window.localStorage.setItem('name', name);

            props.setRegistrationData({
                name,
                yearOfBirth,
                genderAtBirth: selectedGender
            })
            props.setRegistrationFormFilled(true);
            props.setNewCurrentStep(1);
        } catch (error) {
            console.error('Error while sending data:', error);
        } finally {
            setLoaderIsActive(false);
        }
    }

    const conditionsArePassed = (age: number, selectedGender: string): boolean => {
        props.setRegistrationData({
            name,
            yearOfBirth,
            genderAtBirth: selectedGender
        });

        if ((age < 30 || age > 80) && selectedGender === 'Male') {
            props.openModal(t("Sex and Age Error"));
            return false;
        }

        if (age < 30 || age > 80) {
            props.openModal(t("Age Error"));
            return false;
        }

        if (selectedGender === 'Male') {
            props.openModal(t("Sex Error"));
            return false;
        }

        return true;
    }

    const handleNameChange = (value: string): void => {
        setName(value.replace(/[^a-zA-ZÀ-ÿ\s]/g, ''));
    }

    useEffect( () => {
         getPageContent();

        if (props.registrationData) {
            setName(props.registrationData.name);
            setYearOfBirth(props.registrationData.yearOfBirth);
            setSelectedGender(props.registrationData.genderAtBirth);
        }
    }, [])

    if (fields) {
        return (
            <div className="Registration">

                {
                    props.isMobileScreen &&
                    <RegistrationMobile
                        fields={fields}
                        nextStep={props.setNewCurrentStep}
                        registrationFormFilled={props.registrationFormFilled}
                        setRegistrationFormFilled={props.setRegistrationFormFilled}
                        registrationData={props.registrationData}
                        setRegistrationData={props.setRegistrationData}
                    />
                }

                {
                    !props.isMobileScreen &&
                    <div className='position-relative'>
                        <div className='description-body d-flex mb-2'>
                            <img
                                className='logo-2 img-fluid mr-5'
                                src={logo2}
                            />

                            <div className='description'>
                               <p>{t('Registration Content')}</p>
                            </div>
                        </div>

                        <div>
                            <div className="box-1 mb-2">
                                <label className='d-flex justify-content-between align-items-center'>
                                    <div className='mr-5'>{fields["name"].label}</div>

                                    <input
                                        className='input-1 w-100 mr-2'
                                        value={name}
                                        onChange={(e) => handleNameChange(e.target.value)}
                                        type="text"
                                        required={fields.name.required}
                                    />
                                </label>

                                {
                                    fields.name.tooltip &&
                                    <div className='hint-1'>
                                        {fields.name.tooltip}
                                    </div>
                                }
                            </div>

                            <div className="box-1 mb-2">
                                <label className='mb-2 d-flex justify-content-between align-items-center'>
                                    <div className='mr-5'>{fields.yearOfBirth.label}</div>

                                    <input
                                        className='input-1 w-100 mr-2'
                                        value={yearOfBirth}
                                        onChange={(e) => setYearOfBirth(parseInt(e.target.value))}
                                        type="number"
                                        min='1940'
                                        max={new Date().getFullYear()}
                                        step='1'
                                        required={fields.yearOfBirth.required}
                                    />
                                </label>

                                {
                                    fields.yearOfBirth.tooltip &&
                                    <div className="hint-1">
                                        {fields.yearOfBirth.tooltip}
                                    </div>
                                }
                            </div>

                            <div className="box-1 mb-2">
                                <div className='d-flex'>
                                    <span className='label-1 mr-5 w-50'>{fields.genderAtBirth.label}</span><br></br>

                                    <div className='w-50 d-flex'>
                                        <label className='mr-5 d-flex'>
                                            <input
                                                className='d-none'
                                                type="radio"
                                                name="gender"
                                                value="Female"
                                                checked={selectedGender === 'Female'}
                                                onChange={(e) => handleGenderChange(e)}
                                            />

                                            <div
                                                className={selectedGender === 'Female' ? 'checkbox-1 checkbox-1--checked mr-15' : 'checkbox-1 mr-15'}
                                                role='button'
                                            ></div>

                                            <span role='button'>{fields.genderAtBirth.options['Female']}</span>
                                        </label>

                                        <label className='d-flex'>
                                            <input
                                                className='d-none'
                                                type="radio"
                                                name="gender"
                                                value='Male'
                                                checked={selectedGender === 'Male'}
                                                onChange={(e) => handleGenderChange(e)}
                                            />

                                            <div
                                                className={selectedGender === 'Male' ? 'checkbox-1 checkbox-1--checked mr-15' : 'checkbox-1 mr-15'}
                                                role='button'
                                            ></div>

                                            <span role='button'>{fields.genderAtBirth.options['Male']}</span>
                                        </label>
                                    </div>
                                </div>

                                {
                                    fields.genderAtBirth.tooltip &&
                                    <div className="hint-1 mt-2">
                                        {fields.age.tooltip}
                                    </div>
                                }
                            </div>
                        </div>

                        <p className='note text-center mb-7'>{t("Registration Form Note")}</p>

                        <div className='w-100 d-flex flex-column align-items-center'>
                            {
                                formError &&
                                <div className='form-error mb-1'>{formError}</div>
                            }

                            <button
                                className='button-1'
                                onClick={() => submitForm()}
                            >
                                {t("Submit Button")}
                            </button>
                        </div>

                        {loaderIsActive && <div className='submitting-overlay'></div>}
                    </div>
                }
            </div>
        );
    } else {
        return (
            <div className='submitting-overlay-container position-relative'>
                <div className="submitting-overlay"></div>
            </div>
        )
    }
}

export default Registration;
